import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, X, Send, Phone, Plus, ChevronRight } from "lucide-react";

const STATUS_BADGE = {
  open: "bg-amber-100 text-amber-800 border-amber-400",
  pending: "bg-blue-100 text-asap-blue border-asap-blue",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-500",
  closed: "bg-slate-100 text-slate-600 border-slate-400",
};

export default function SupportDrawer({ open, onClose, embedded = false }) {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [creating, setCreating] = useState(false);
  const [newSubj, setNewSubj] = useState("");
  const [newMsg, setNewMsg] = useState("");
  const [err, setErr] = useState("");

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/tickets/my");
      setTickets(data);
    } catch {}
  };

  useEffect(() => {
    if (open) {
      loadTickets();
      const id = setInterval(loadTickets, 6000);
      return () => clearInterval(id);
    }
  }, [open]);

  useEffect(() => {
    if (active) {
      api.get(`/tickets/${active.id}`).then((r) => setMessages(r.data.messages));
      const id = setInterval(() => api.get(`/tickets/${active.id}`).then((r) => setMessages(r.data.messages)), 4000);
      return () => clearInterval(id);
    }
  }, [active]);

  const submitNew = async () => {
    if (!newSubj.trim() || !newMsg.trim()) return;
    setErr("");
    try {
      const { data } = await api.post("/tickets", { subject: newSubj, message: newMsg });
      setNewSubj(""); setNewMsg(""); setCreating(false);
      setTickets((t) => [data, ...t]);
      setActive(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    try {
      const { data } = await api.post(`/tickets/${active.id}/reply`, { message: reply });
      setMessages((m) => [...m, data]);
      setReply("");
    } catch {}
  };

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 ${embedded ? "" : "bg-black/40"}`} onClick={onClose} data-testid="support-drawer">
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white border-l-4 border-asap-yellow shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="bg-asap-deep text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare size={20} className="text-asap-yellow"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-asap-yellow font-bold">Support</div>
              <div className="font-display uppercase">Help Center</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="support-close" className="p-1 hover:bg-white/10 rounded"><X size={20}/></button>
        </header>

        {!active ? (
          <>
            <div className="px-5 py-3 border-b-2 border-asap-deep flex items-center gap-3">
              <a href="tel:+18317477141" className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-sm border-2 border-asap-deep hover:bg-asap-orange hover:border-asap-orange transition" data-testid="support-call-quick">
                <Phone size={14}/> Call 831-747-7141
              </a>
              <button onClick={() => setCreating(true)} data-testid="support-new" className="inline-flex items-center gap-2 px-3 py-2 border-2 border-asap-deep text-asap-deep font-display uppercase text-sm hover:bg-asap-deep hover:text-white">
                <Plus size={14}/> New
              </button>
            </div>

            {creating && (
              <div className="p-4 border-b-2 border-slate-200 bg-asap-yellow/10 space-y-3" data-testid="support-new-form">
                <input value={newSubj} onChange={(e) => setNewSubj(e.target.value)} placeholder="Subject" className="w-full px-3 py-2 border-2 border-asap-deep outline-none" data-testid="support-subject"/>
                <textarea value={newMsg} onChange={(e) => setNewMsg(e.target.value)} rows={4} placeholder="Describe the issue…" className="w-full px-3 py-2 border-2 border-asap-deep outline-none resize-none" data-testid="support-message"/>
                {err && <div className="text-asap-red text-sm font-bold">{err}</div>}
                <div className="flex gap-2">
                  <button onClick={submitNew} data-testid="support-submit" className="flex-1 px-3 py-2 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep">Send</button>
                  <button onClick={() => { setCreating(false); setErr(""); }} className="px-3 py-2 border-2 border-slate-300">Cancel</button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="ticket-list">
              {tickets.length === 0 && !creating && (
                <div className="p-8 text-center text-slate-400 text-sm">No tickets yet. Open one above or call us.</div>
              )}
              {tickets.map((t) => (
                <button key={t.id} onClick={() => setActive(t)} className="w-full text-left p-4 hover:bg-asap-yellow/10 flex items-center gap-3" data-testid={`ticket-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-asap-deep truncate">{t.subject}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{new Date(t.updated_at).toLocaleString()}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${STATUS_BADGE[t.status]}`}>{t.status}</span>
                  <ChevronRight size={16} className="text-slate-400"/>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="px-5 py-3 border-b-2 border-asap-deep flex items-center justify-between">
              <button onClick={() => setActive(null)} className="text-xs uppercase tracking-widest font-bold text-asap-orange">← All tickets</button>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${STATUS_BADGE[active.status]}`}>{active.status}</span>
            </div>
            <div className="px-5 py-3 border-b border-slate-200">
              <div className="font-display text-lg text-asap-deep uppercase">{active.subject}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="ticket-thread">
              {messages.map((m) => (
                <div key={m.id} className={`max-w-[80%] ${m.author_id === user.id ? "ml-auto" : ""}`}>
                  <div className={`p-3 rounded ${m.author_id === user.id ? "bg-asap-deep text-white" : "bg-white border-2 border-asap-deep"}`}>
                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{m.author_name} · {m.author_role}</div>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{m.message}</div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t-2 border-asap-deep flex gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Type a reply…" className="flex-1 px-3 py-2 border-2 border-asap-deep outline-none" data-testid="ticket-reply-input"/>
              <button onClick={sendReply} data-testid="ticket-reply-send" className="px-3 py-2 bg-asap-yellow text-asap-deep border-2 border-asap-deep"><Send size={16}/></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
